import { AuthManager } from './services/auth-manager.js';

/**
 * Dashboard Authentication Module
 * Handles user authentication state and related UI updates
 */
export class DashboardAuth {
  constructor(dashboard) {
    this.dashboard = dashboard;
    this.authManager = null;
    this.lastAuthUserId = null;
    this.hasShownSignedOutMessage = false;
  }

  async initialize() {
    this.authManager = new AuthManager({
      onAuthChange: (user) => this.handleAuthStateChange(user),
    });

    // Initialize authentication overlay and account controls
    this.dashboard.authManager = this.authManager;
  }

  handleAuthStateChange(user) {
    const currentId = user?.uid ?? null;
    if (currentId === this.lastAuthUserId) {
      return;
    }

    this.lastAuthUserId = currentId;

    if (user) {
      const identifier = user.displayName || user.email || 'your account';
      const isLocalUser = user.uid === 'local-user';
      this.hasShownSignedOutMessage = false;
      document.body.dataset.auth = 'authenticated';
      console.log('[DashboardAuth] User authenticated, hiding main dashboard');
      this.dashboard.showNotification(
        isLocalUser
          ? `🖥️ Running in local mode as ${identifier}`
          : `🔐 Signed in as ${identifier}`
      );

      // Force header to expanded state immediately for authenticated users
      this.forceHeaderExpanded();
    } else {
      this.hasShownSignedOutMessage = true;
      delete document.body.dataset.auth;
      console.log('[DashboardAuth] User signed out, showing main dashboard');

      // Force header to collapsed state for signed out users
      this.forceHeaderCollapsed();

      this.dashboard.showNotification('🔓 Signed out. Data will stay on this device until you sign in.');
    }

    // Update settings menu with user info
    this.updateSettingsUserInfo(user);
  }

  forceHeaderExpanded() {
    const welcomeSection = document.getElementById('welcome-section');
    const headerToggle = document.getElementById('header-toggle');

    if (welcomeSection) {
      welcomeSection.classList.remove('collapsed');
    }

    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.header = 'expanded';
    }

    // Update toggle button state
    if (headerToggle) {
      const icon = headerToggle.querySelector('.toggle-icon');
      if (icon) {
        icon.textContent = '▲';
      }
      headerToggle.dataset.state = 'expanded';
      headerToggle.setAttribute('aria-expanded', 'true');
      headerToggle.setAttribute('aria-label', 'Collapse header');
      headerToggle.title = 'Collapse header';
    }

    console.log('[DashboardAuth] Header forced to expanded state');
  }

  forceHeaderCollapsed() {
    const welcomeSection = document.getElementById('welcome-section');
    const headerToggle = document.getElementById('header-toggle');

    if (welcomeSection) {
      welcomeSection.classList.add('collapsed');
    }

    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.header = 'collapsed';
    }

    // Update toggle button state
    if (headerToggle) {
      const icon = headerToggle.querySelector('.toggle-icon');
      if (icon) {
        icon.textContent = '▼';
      }
      headerToggle.dataset.state = 'collapsed';
      headerToggle.setAttribute('aria-expanded', 'false');
      headerToggle.setAttribute('aria-label', 'Expand header');
      headerToggle.title = 'Expand header';
    }

    console.log('[DashboardAuth] Header forced to collapsed state');
  }

  handleLogout() {
    if (this.authManager && this.authManager.auth) {
      this.authManager.handleSignOut();
      this.dashboard.closeSettingsMenu();
    }
  }

  updateSettingsUserInfo(user) {
    const userInfoDiv = document.getElementById('settings-user-info');
    const greetingSpan = document.getElementById('user-greeting');
    const emailSpan = document.getElementById('user-email');

    if (userInfoDiv && greetingSpan && emailSpan) {
      if (user) {
        userInfoDiv.style.display = 'block';
        const displayName = user.displayName;
        greetingSpan.textContent = displayName ? `Hi, ${displayName}!` : 'Hi 👋';
        emailSpan.textContent = user.email ?? 'Signed in';
      } else {
        userInfoDiv.style.display = 'none';
      }
    }
  }

  get isAuthenticated() {
    return Boolean(this.authManager?.user) || Boolean(document.body.dataset.auth);
  }

  get currentUser() {
    return this.authManager?.user || null;
  }
}
